import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";

export type AccentColor = {
  name: string;
  nameAr: string;
  hex: string;
  oklch: string;
  glow: string;
};

export const ACCENT_COLORS: AccentColor[] = [
  { name: "Teal",    nameAr: "فيروزي",  hex: "#299e94", oklch: "oklch(0.62 0.10 188)", glow: "oklch(0.72 0.12 188)" },
  { name: "Red",     nameAr: "أحمر",    hex: "#e50914", oklch: "oklch(0.58 0.23 27)",  glow: "oklch(0.68 0.24 27)" },
  { name: "Gold",    nameAr: "ذهبي",    hex: "#f5a524", oklch: "oklch(0.78 0.16 75)",  glow: "oklch(0.85 0.17 75)" },
  { name: "Purple",  nameAr: "بنفسجي",  hex: "#8b5cf6", oklch: "oklch(0.65 0.22 295)", glow: "oklch(0.75 0.20 295)" },
  { name: "Blue",    nameAr: "أزرق",    hex: "#3b82f6", oklch: "oklch(0.62 0.20 255)", glow: "oklch(0.72 0.18 255)" },
  { name: "Pink",    nameAr: "وردي",    hex: "#ec4899", oklch: "oklch(0.66 0.22 350)", glow: "oklch(0.76 0.20 350)" },
  { name: "Green",   nameAr: "أخضر",    hex: "#10b981", oklch: "oklch(0.68 0.16 165)", glow: "oklch(0.78 0.16 165)" },
];

type Dir = "rtl" | "ltr";
export type ThemeMode = "light" | "dark" | "system";
export type LanguageCode = "ar" | "en" | "fr";

export const LANGUAGE_OPTIONS: { code: LanguageCode; label: string; native: string; dirName: "rtl" | "ltr" }[] = [
  { code: "ar", label: "Arabic",  native: "العربية", dirName: "rtl" },
  { code: "en", label: "English", native: "English", dirName: "ltr" },
  { code: "fr", label: "French",  native: "Français", dirName: "ltr" },
];

// Migrate any old "fe" value from localStorage to the correct "fr".
const normalizeLanguageCode = (raw: string | null): LanguageCode | null => {
  if (!raw) return null;
  if (raw === "fe") return "fr";
  if (raw === "ar" || raw === "en" || raw === "fr") return raw;
  return null;
};

type ThemeContextValue = {
  accentIndex: number;
  accent: AccentColor;
  setAccent: (i: number) => void;
  autoCycle: boolean;
  toggleAutoCycle: () => void;
  dir: Dir;
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
  toggleDir: () => void;
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  isDark: boolean;
  toggleDark: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const applyAccent = (c: AccentColor) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--primary", c.oklch);
  root.style.setProperty("--primary-glow", c.glow);
  root.style.setProperty("--ring", c.oklch);
};

const dirFromLanguage = (code: LanguageCode): Dir => (code === "ar" ? "rtl" : "ltr");

const isLanguageCode = (value: string | null): value is LanguageCode => (
  !!value && LANGUAGE_OPTIONS.some((option) => option.code === value)
);
// referenced for typing safety; consumer code uses normalizeLanguageCode.
void isLanguageCode;

const applyLanguage = (code: LanguageCode) => {
  if (typeof document === "undefined") return;
  document.documentElement.dir = dirFromLanguage(code);
  document.documentElement.lang = code;
};

const resolveDark = (mode: ThemeMode): boolean => {
  if (typeof window === "undefined") return true;
  if (mode === "system") return window.matchMedia("(prefers-color-scheme: dark)").matches;
  return mode === "dark";
};

const applyDark = (dark: boolean) => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", dark);
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [accentIndex, setAccentIndex] = useState(0);
  const [autoCycle, setAutoCycle] = useState(false);
  const [dir, setDir] = useState<Dir>("rtl");
  const [language, setLanguageState] = useState<LanguageCode>("ar");
  const [mode, setModeState] = useState<ThemeMode>("system");
  const [isDark, setIsDark] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const a = Number(localStorage.getItem("rawan:accent") ?? "0");
    const d = (localStorage.getItem("rawan:dir") as Dir) ?? "rtl";
    const savedLanguage = normalizeLanguageCode(
      localStorage.getItem("rawan:language") ?? localStorage.getItem("rawan:lang"),
    );
    const nextLanguage: LanguageCode = savedLanguage ?? (d === "rtl" ? "ar" : "en");
    const savedMode = localStorage.getItem("rawan:mode") as ThemeMode | null;
    const m: ThemeMode = (savedMode === "light" || savedMode === "dark" || savedMode === "system") ? savedMode : "system";
    const ac = localStorage.getItem("rawan:auto") === "1";
    setAccentIndex(Number.isFinite(a) ? a : 0);
    setLanguageState(nextLanguage);
    setDir(dirFromLanguage(nextLanguage));
    setModeState(m);
    setAutoCycle(ac);
  }, []);

  useEffect(() => { applyAccent(ACCENT_COLORS[accentIndex] ?? ACCENT_COLORS[0]); localStorage.setItem("rawan:accent", String(accentIndex)); }, [accentIndex]);
  useEffect(() => {
    const nextDir = dirFromLanguage(language);
    setDir(nextDir);
    applyLanguage(language);
    localStorage.setItem("rawan:language", language);
    // Backward-compatible alias used by the first-paint boot script and old builds.
    localStorage.setItem("rawan:lang", language);
    localStorage.setItem("rawan:dir", nextDir);
  }, [language]);

  // Mode → dark class, with system listener
  useEffect(() => {
    localStorage.setItem("rawan:mode", mode);
    const dark = resolveDark(mode);
    setIsDark(dark);
    applyDark(dark);
    if (mode !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => { setIsDark(e.matches); applyDark(e.matches); };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [mode]);

  useEffect(() => { localStorage.setItem("rawan:auto", autoCycle ? "1" : "0"); }, [autoCycle]);

  useEffect(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoCycle) {
      timerRef.current = setInterval(() => {
        setAccentIndex((i) => (i + 1) % ACCENT_COLORS.length);
      }, 5000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoCycle]);

  const value: ThemeContextValue = {
    accentIndex,
    accent: ACCENT_COLORS[accentIndex] ?? ACCENT_COLORS[0],
    setAccent: useCallback((i: number) => setAccentIndex(i), []),
    autoCycle,
    toggleAutoCycle: useCallback(() => setAutoCycle((v) => !v), []),
    dir,
    language,
    setLanguage: useCallback((code: LanguageCode) => setLanguageState(code), []),
    toggleDir: useCallback(() => setLanguageState((code) => (code === "ar" ? "en" : "ar")), []),
    mode,
    setMode: useCallback((m: ThemeMode) => setModeState(m), []),
    isDark,
    toggleDark: useCallback(() => setModeState((m) => (resolveDark(m) ? "light" : "dark")), []),
  };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
